import { makeAutoObservable, reaction, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { RequestCancelAxios, SingletonRequest } from 'shared/api/lib'
import { type IInboxCombine, type IInboxData, type TYPES_INBOX } from 'entities/Inbox'
import { InboxesApi } from 'entities/Inbox/api/inboxes'
import { UsersApi } from 'entities/Users'
import { Inbox } from 'entities/Inbox/model/Inbox'
import type {
  IParamsTeamsAll,
  IResponseEventInboxCalling,
  IResponseInboxCombine,
} from 'entities/Inbox/api/types'
import { UnifiedInbox } from 'entities/Inbox/model/UnifiedInbox'
import { GroupInbox } from 'entities/Inbox/model/GroupInbox'
import { ChannelsInboxes } from 'entities/Inbox/channels/inboxes'
import { type Phone } from 'entities/Phone/model/Phone'
import type { EntitiesStore } from 'entities/EntitiesExport'
import { PhoneApi } from 'entities/Phone'
import { LocalInboxesStore } from 'entities/Inbox/store/LocalInboxesStore'
import { OrganizationInboxesStore } from 'entities/Inbox/store/OrganizationInboxesStore'

export class InboxesStore {
  initPromise
  private _setFinishInit: ((value?: unknown) => void) | null = null
  private _loading = false
  private _inboxesMap: Map<number, IInboxCombine> = new Map()
  private _currentInboxId: number | null = null
  private _currentInboxType: TYPES_INBOX | null = null
  private _currentInboxTypeId: number | null = null
  private _disposeCurrentInboxId: ReturnType<typeof reaction> | null = null
  private _inboxesLoadingIds: Set<number> = new Set()
  private readonly _requestCancelAxios = new RequestCancelAxios()

  readonly channelsInboxes: ChannelsInboxes
  readonly localInboxes: LocalInboxesStore
  readonly organizationInboxesStore: OrganizationInboxesStore

  constructor(private _entities: EntitiesStore) {
    this.localInboxes = new LocalInboxesStore(this._entities)
    this.organizationInboxesStore = new OrganizationInboxesStore(this, this._entities)
    this.channelsInboxes = new ChannelsInboxes(this, this._entities.websocket)

    this.reactionCurrentInboxId()

    this.initPromise = new Promise((resolve) => {
      this._setFinishInit = resolve
    })
    makeAutoObservable(this)
  }

  get hasRcsInbox() {
    return !!this.rcsInbox
  }

  get rcsInbox() {
    let rcsInbox: Inbox | null | undefined = this.inboxesList
      .reduce<Inbox[]>((state, item) => {
        if (item instanceof Inbox) state.push(item)

        return state
      }, [])
      .find((inbox) => inbox.isRCS)

    if (!rcsInbox && this._entities.usersStore.user?.isAdminOrOwnerRole) {
      rcsInbox = this._entities.rcsStore.rcsOrganizationInbox
    }

    if (!rcsInbox) return null

    return rcsInbox
  }

  private _fetchInboxes = async () => {
    if (this.inboxesList.length) {
      this._setFinishInit?.()
      return
    }

    try {
      runInAction(() => {
        this._loading = true
      })

      await Promise.all([this.fetchUser(), this.fetchSmartInboxes(), this.fetchGroupInboxes()])

      this.channelsInboxes.subscribeChannels()

      runInAction(() => {
        this._loading = false
      })
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
      this._setFinishInit?.()
    }
  }
  fetchInboxes = new SingletonRequest(this._fetchInboxes).request

  private _fetchUser = async () => {
    try {
      const userResponse = await this._entities.usersStore.fetchUsersMe()

      if (userResponse) {
        this.getById(userResponse.currentInboxId, true)
      }

      runInAction(() => {
        if (userResponse) {
          this._entities.usersStore.addItem(userResponse)
          this.initInboxCurrent(userResponse)
        }
      })
    } catch (e) {
      logger.error(e)
    }
  }

  fetchUser = new SingletonRequest(this._fetchUser).request

  private _fetchSmartInboxes = async () => {
    try {
      const { data } = await InboxesApi.getInboxesPersonal({
        withUnified: true,
      })

      this.addItems(data)
    } catch (e) {
      console.log(e)
    }
  }

  fetchSmartInboxes = new SingletonRequest(this._fetchSmartInboxes).request

  private _fetchGroupInboxes = async () => {
    try {
      const { data } = await InboxesApi.getGroupInboxes()

      this.addItems(data)
    } catch (e) {
      console.log(e)
    }
  }

  fetchGroupInboxes = new SingletonRequest(this._fetchGroupInboxes).request

  reFetchInboxes = async () => {
    const [inboxesData] = await Promise.all([
      InboxesApi.getInboxesPersonal({
        withUnified: true,
      }),
    ])

    const inboxes = inboxesData.data

    this.addItems(inboxes)
  }

  initInboxCurrent = (data: IInboxData) => {
    this._currentInboxId = data.currentInboxId
    this._currentInboxType = data.currentInboxType
    this._currentInboxTypeId = data.currentInboxTypeId

    if (this._currentInboxId) {
      this.localInboxes.resetCurrentInbox()
    } else {
      this.localInboxes.initInboxQuickFilter()
    }
  }

  reset = () => {
    this._inboxesMap.clear()
    this.organizationInboxesStore.reset()
  }

  isGroupChild = (id: number | string): boolean => {
    const inboxesGroups = this.inboxesList.filter((item) => item.isGroupInbox)

    let isChildInbox = false
    inboxesGroups.forEach((inboxGroup) => {
      if (inboxGroup.type === 'inbox_group') {
        inboxGroup.teamsIds.forEach((item) => {
          if (id === item) {
            isChildInbox = true
          }
        })
      }
    })

    return isChildInbox
  }

  hasActiveChildInbox = (id?: number): boolean => {
    if (!id) return false

    const inbox = this._inboxesMap.get(id)
    if (!inbox || !inbox.isGroupInbox || !this._currentInboxId) return false

    return inbox.teamsIds.some((teamId) => teamId === this._currentInboxId)
  }

  isChildOfActiveGroupInbox = (id?: number | string): boolean => {
    if (!this.currentInbox?.isGroupInbox || !id) return false

    return this.currentInbox.teamsIds.some((teamId) => teamId === id)
  }

  isChildOfGroupInboxWithActiveChild = (id?: number): boolean => {
    if (!id) return false

    const inbox = this._inboxesMap.get(id)
    if (!inbox || inbox.isGroupInbox || !this._currentInboxId) return false

    const inboxesGroups = this.inboxesList.filter((item) => item.isGroupInbox)
    const activeGroupInbox = inboxesGroups.find((item) =>
      item.teamsIds.some((teamId) => teamId === this._currentInboxId)
    )

    if (!activeGroupInbox) return false
    return activeGroupInbox.teamsIds.some((teamId) => teamId === id)
  }

  addItems = (items: IResponseInboxCombine[]) => {
    items.forEach((item) => {
      if (item.type === 'inbox') {
        this._entities.numbersStore.addItems(item.numbers)
      }

      this.addItem(item)
    })

    return items.reduce<IInboxCombine[]>((state, item) => {
      const inbox = this.getItem(item.id)

      return inbox ? [...state, inbox] : [...state]
    }, [])
  }

  deleteItem = (id: number) => {
    this._inboxesMap.delete(id)

    if (this.organizationInboxesStore.loaded) {
      this.organizationInboxesStore.deleteItem(id)
    }
  }

  deleteItemFromGroup = (idGroup: number, idChild: number) => {
    const itemGroup = this._inboxesMap.get(idGroup)

    if (itemGroup instanceof GroupInbox) {
      itemGroup.removeTeamId(idChild)
    }
  }

  getItem = (id?: number | null) => {
    if (typeof id !== 'number') return

    return this._inboxesMap.get(id)
  }

  getOrganizationItem = (id?: number | null) => {
    if (typeof id !== 'number') return

    return this._inboxesMap.get(id) ?? this.organizationInboxesStore.getItem(id)
  }

  getItemShared = (id?: number) => {
    if (!id) return

    const inbox = this._inboxesMap.get(id)

    if (inbox?.type !== 'inbox') return

    return inbox
  }

  createInboxItem = (data: IResponseInboxCombine) => {
    if (data.type === 'unified-inbox') {
      return new UnifiedInbox(data, this._entities)
    }
    if (data.type === 'inbox_group') {
      return new GroupInbox(data, this._entities)
    }
    return new Inbox(data, this._entities)
  }

  addItem = (data: IResponseInboxCombine) => {
    const existing = this._inboxesMap.get(data.id)

    if (existing) {
      if (existing instanceof Inbox && data.type === 'inbox') {
        existing.syncOrigin({ ...existing, ...data })
      }
      if (existing instanceof GroupInbox && data.type === 'inbox_group') {
        existing.syncOrigin({ ...existing, ...data })
      }
      if (existing instanceof UnifiedInbox && data.type === 'unified-inbox') {
        existing.syncOrigin({ ...existing, ...data })
      }
    } else {
      const currentUserId = this._entities.usersStore.user?.id
      const isOrganizationOnlyInbox =
        data.type === 'inbox' && currentUserId && !data.member_ids.includes(currentUserId)

      if (!isOrganizationOnlyInbox) {
        const created = this.createInboxItem(data)
        this._inboxesMap.set(data.id, created)

        if (created instanceof Inbox && data.type === 'inbox') {
          this._entities.numbersStore.addItems(data.numbers)
        }
      }
    }

    if (this.organizationInboxesStore.loaded && data.type === 'inbox') {
      this.organizationInboxesStore.syncItem(data)
    }

    return this._inboxesMap.get(data.id)
  }

  handleUpdateTeamInbox = async (inboxId: number, type: 'default' | 'fast' = 'default') => {
    if (this._currentInboxId === inboxId) return

    try {
      const inbox = await this.getById(inboxId)

      if (inbox) {
        this._requestCancelAxios.init()

        if (inbox.id) {
          this.localInboxes.resetCurrentInbox()
        }

        if (type === 'fast') {
          runInAction(() => {
            this._currentInboxId = inbox.id
            this._currentInboxType = inbox.inboxType
            this._currentInboxTypeId = inbox.id
          })
        }

        await UsersApi.updateUsersTeam(inbox.bodyUpdateUserTeam, {
          cancelToken: this._requestCancelAxios.token,
        })

        if (type === 'default') {
          runInAction(() => {
            this._currentInboxId = inbox.id
            this._currentInboxType = inbox.inboxType
            this._currentInboxTypeId = inbox.id
          })
        }
      }
    } catch (e) {
      logger.error(e)
    }
  }

  handleUpdateTeamsFavorite = async (inbox: IInboxCombine, status: boolean) => {
    if (inbox.is_favorite === status) return

    await InboxesApi.updateTeamsFavorite(inbox.bodyUpdateTeamsFavorite)

    runInAction(() => {
      inbox.is_favorite = status
    })
  }

  handleCreateTeamsGroupsByIdAddTeams = async (inbox: GroupInbox, groupId: number) => {
    const groupInbox = this.getItem(groupId)

    if (groupInbox && groupInbox.type === 'inbox_group') {
      if (groupInbox.teamsIds.find((teamId) => teamId === inbox.id)) return

      await InboxesApi.createTeamsGroupsByIdAddTeams(
        groupId,
        inbox.bodyCreateTeamsGroupsByIdAddTeams
      )

      if (!groupInbox.teamsIds.find((teamId) => teamId === inbox.id)) {
        groupInbox.teamsIds.push(inbox.id)
      }
    }
  }

  handleDeleteTeamsGroupsByIdRemoveTeamById = async (
    inbox: IInboxCombine,
    groupInbox?: IInboxCombine
  ) => {
    if (!groupInbox) return
    if (groupInbox.type !== 'inbox_group') return

    await InboxesApi.deleteTeamsGroupsByIdRemoveTeamById(groupInbox.id, inbox.id)
    groupInbox.removeTeamId(inbox.id)
  }

  handleDeleteTeamsGroupsById = async (inbox: IInboxCombine) => {
    await UsersApi.updateUsersTeam({
      id: 0,
      type: 3,
    })
    InboxesApi.deleteTeamsGroupsById(inbox.id)
  }

  handleUpdateTeamsGroupsById = async (name: string, inbox: IInboxCombine) => {
    if (inbox.name === name) {
      this.handleRenameGroup(inbox, false)

      return
    }

    InboxesApi.updateTeamsGroupsById(inbox.id, {
      ...inbox.bodyUpdateTeamsGroupsById,
      name: name,
    })

    inbox.name = name

    this.handleRenameGroup(inbox, false)
  }

  handleRenameGroup = async (inbox: IInboxCombine, status: boolean) => {
    if (inbox.type !== 'inbox_group') return

    runInAction(() => {
      inbox.is_rename = status
    })
  }

  handleUpdateTeamsMute = async (inbox: IInboxCombine, status: boolean) => {
    if (inbox.type !== 'inbox') return
    if (inbox.isMuted === status) return

    await InboxesApi.updateTeamsMute(inbox.id, {
      is_muted: inbox.isMuted ? 0 : 1,
    })

    runInAction(() => {
      inbox.handleUpdateIsMuted(status)
    })
  }

  getById = async (id: number, update?: boolean, withError?: boolean) => {
    try {
      let team = this.getItem(id)

      if (id && !team && !this._inboxesLoadingIds.has(id)) {
        this._inboxesLoadingIds.add(id)
        const { data } = await InboxesApi.getTeamById(id)

        team = new Inbox(data, this._entities)

        if (update) {
          this.addItem(data)
        }
      }

      return team
    } catch (e) {
      logger.error(e)
      if (withError) {
        throw e
      }
    } finally {
      runInAction(() => {
        this._inboxesLoadingIds.delete(id)
      })
    }
  }

  fetchInboxesByIds = async (ids: number[]): Promise<IInboxCombine[]> => {
    const inboxes = await Promise.all(
      ids.map(async (id) => {
        try {
          return await this.getById(id, true)
        } catch (error) {
          console.error(`Failed to fetch inbox ${id}:`, error)
          return null
        }
      })
    )
    return inboxes.filter((inbox): inbox is IInboxCombine => inbox !== null && inbox !== undefined)
  }

  getInboxByNumber = (numberId: number) => {
    const inboxes = this.inboxesList.reduce<Inbox[]>((state, item) => {
      if (item instanceof Inbox) state.push(item)

      return state
    }, [])

    return inboxes.find((item) => item.numberIds.includes(numberId))
  }

  getInboxByNumberAsync = async (numberId: number) => {
    try {
      const inbox = this.getInboxByNumber(numberId)

      if (!inbox) {
        const response = await PhoneApi.getTeamByNumberId(numberId)

        return this.addItem(response.data.data)
      }

      return inbox
    } catch (e) {
      logger.error(e)
    }
  }

  updateInboxCalling = (data: IResponseEventInboxCalling) => {
    const inbox = this.getItem(data.team_id)

    if (inbox?.type === 'inbox') {
      const number = this._entities.numbersStore.getItem(inbox?.numberId)

      if (number) {
        number.updateInboxCalling(data.number)
      }
    }
  }

  reactionCurrentInboxId = () => {
    this._disposeCurrentInboxId?.()
    this._disposeCurrentInboxId = reaction(
      () => this._currentInboxId,
      async (currentInboxId) => {
        if (!currentInboxId) return

        let inbox = this.getItem(currentInboxId)

        if (inbox instanceof GroupInbox) {
          inbox = this.getItem(inbox.teamsIds[0])
        }

        if (!inbox) return

        try {
          const { data } = await InboxesApi.getTeamByIdMembers(inbox.id)
          this._entities.usersStore.addItems(data)
        } catch (e) {
          console.log('ERROR getTeamByIdMembers: ', e)
        }
      }
    )
  }

  get isUnifiedInboxOnly() {
    const accessibleInboxes = this.inboxesList.filter((inbox) => {
      if (inbox instanceof GroupInbox) {
        return inbox.teamsIds.some((id) => !!this.getItem(id))
      }

      return true
    })

    return accessibleInboxes.length === 1 && accessibleInboxes[0].type === 'unified-inbox'
  }

  get currentInbox() {
    if (this._currentInboxId === null) return

    return this.getItem(this._currentInboxId)
  }

  get isUnifiedInbox() {
    return this._currentInboxId === 0
  }

  get inboxesList() {
    return Array.from(this._inboxesMap.values())
  }

  get sharedInboxes() {
    return this.inboxesList.reduce<Inbox[]>((state, inbox) => {
      if (inbox.type === 'inbox') {
        state.push(inbox)
      }

      return state
    }, [])
  }

  get groupsInboxes() {
    return this.inboxesList.reduce<GroupInbox[]>((state, inbox) => {
      if (inbox.type === 'inbox_group') {
        state.push(inbox)
      }

      return state
    }, [])
  }

  get unifiedInboxes() {
    return this.inboxesList.reduce<UnifiedInbox[]>((state, inbox) => {
      if (inbox.type === 'unified-inbox') {
        state.push(inbox)
      }

      return state
    }, [])
  }

  get onlyInboxesList() {
    return this.inboxesList.reduce<Inbox[]>((state, inbox) => {
      if (inbox.type === 'inbox') {
        state.push(inbox)
      }

      return state
    }, [])
  }

  get hasInboxWithCurrentUserAsOwner() {
    const currentUserId = this._entities.usersStore.user?.id

    return this.onlyInboxesList.some((inbox) => inbox.ownerId === currentUserId)
  }

  get selectInbox() {
    return this.getItem(this._currentInboxId)
  }

  get selectCurrentInboxId() {
    return this._currentInboxId
  }

  get nonPFTInboxNumberType() {
    const inbox = this.inboxesList.find((inbox) => !inbox.isPFTInbox && inbox.type === 'inbox')

    if (!(inbox instanceof Inbox)) return undefined

    return inbox.number?.type
  }

  get firstInboxId() {
    const inboxIds = this.inboxesList.reduce<number[]>((acc, data) => {
      if (data?.type === 'unified-inbox') return acc

      if (data?.type === 'inbox_group') {
        if (data.teamsIds.length) {
          const sortedTeams = data.origin.teams.sort((a, b) => a.name.localeCompare(b.name))
          const teamInbox = this.getItem(sortedTeams[0].id)

          if (!teamInbox) return acc
          if (teamInbox.isFavoriteType) return [teamInbox.id, ...acc]
          if (!teamInbox.isFavoriteType) return [...acc, teamInbox.id]

          return acc
        }
      }

      if (data?.type !== 'inbox') return acc
      if (data.isFavoriteType) return [data.id, ...acc]
      if (!data.isFavoriteType) return [...acc, data.id]

      return acc
    }, [])

    return inboxIds[0]
  }

  get sortedFirstInbox() {
    return this.getItem(this.firstInboxId)
  }

  getNumbersByTeamIds = (teamIds: number[]) => {
    let numbers: Phone[] = []
    const inboxes = teamIds.map((id) => this.getItem(id))
    inboxes.forEach((inbox) => {
      if (inbox instanceof Inbox) {
        numbers = numbers.concat(inbox.numbers)
      }
    })
    return numbers
  }

  get unreadMessagesCount() {
    return Array.from(this._inboxesMap.values())
      .map((item) => item.unreadCount)
      .filter((item) => item)
      .reduce((state, value) => state + value, 0)
  }

  get inboxesMap() {
    return this._inboxesMap
  }

  get currentInboxId() {
    return this._currentInboxId
  }

  get currentInboxTypeId() {
    return this._currentInboxTypeId
  }

  get currentInboxType() {
    return this._currentInboxType
  }

  get loading() {
    return this._loading
  }

  get organizationInboxes() {
    return this.organizationInboxesStore.items
  }

  get organizationInboxesLoading() {
    return this.organizationInboxesStore.loading
  }

  private _getTeamsAll = (params: IParamsTeamsAll) => {
    return InboxesApi.getTeamsAll(params)
  }

  getTeamsAll = new SingletonRequest(this._getTeamsAll).request

  getIntegrationTeams = () => InboxesApi.getIntegrationTeams()

  createIntegrationGroup = (params: { integration_team_id: number }) =>
    InboxesApi.createIntegrationGroup(params)

  importIntegrationTeams = () => InboxesApi.importIntegrationTeams()

  refreshIntegrationTeams = (teamId: number) => InboxesApi.refreshIntegrationTeams(teamId)

  refreshIntegrationTeamsList = () => InboxesApi.refreshIntegrationTeamsList()

  refreshIntegrationTeamContacts = (teamId: number) =>
    InboxesApi.refreshIntegrationTeamContacts(teamId)

  updateGroup = (params: {
    group_id: number
    name: string
    description?: string | null
    members: number[]
  }) => InboxesApi.updateGroup(params)

  addGroupMembers = (groupId: number, memberIds: number[]) =>
    InboxesApi.patchGroupMembersAdd(groupId, { members: memberIds })

  removeGroupMembers = (groupId: number, memberIds: number[]) =>
    InboxesApi.patchGroupMembersRemove(groupId, { members: memberIds })
}
